import { css } from "@emotion/react";
import { Fragment, type PropsWithChildren, useEffect, useState } from "react";
import { Modal } from "reshaped";

import { useDeviceWidth } from "../../components/adaptation/utilities";
import { Asset } from "../../components/Asset";
import Await from "../../components/async/Await";
import IconButton from "../../components/Button/IconButton";
import Divider from "../../components/Divider";
import Menu from "../../components/Menu/Menu";
import MenuItem from "../../components/Menu/MenuItem";
import OnlyWithPermission from "../../components/OnlyWithPermission";
import Spacer from "../../components/space/Spacer";
import { WithSpace } from "../../components/space/WithSpace";
import Text from "../../components/Text";
import { useTheme } from "../../components/theme/hooks";
import { callOutgoingRequest } from "../../data/tanstack-query";
import { logout } from "../../endpoints/outgoing-requests";
import { Permission } from "../../enums/Permission";
import useSubmit from "../../hooks/useSubmit";
import { useIsRouteActive } from "../../navigation/hooks";
import { navigate, useLocationPathname } from "../../navigation/location";
import { loggedInHomeRoute } from "../../navigation/routes";
import { contactsNavigator, contactsRoute } from "../../screens/ContactsNavigator/navigation";
import { createProjectRoute } from "../../screens/CreateProjectNavigator/navigation";
import { useCurrentProject } from "../../screens/LoggedInNavigator/services/context";
import { mailingNavigator, mailingRoute } from "../../screens/MailingNavigator/navigation";
import { processRoute } from "../../screens/ProcessNavigator/navigation";
import { indexRoute } from "../../screens/RootNavigator/navigation";
import { settingsRoute } from "../../screens/SettingsNavigator/navigation";
import { usersNavigator, usersRoute } from "../../screens/UsersNavigator/navigation";
import { SafeAreaFrame } from "../SafeArea";
import logo from "./assets/logo.png";

export function WithMainMenu(props: PropsWithChildren) {
    const { children } = props;
    const size = useDeviceWidth();
    if (size === null) return;
    return (
        <Fragment>
            {size === "large" ? <MainMenuAsSideBar /> : <MainMenuAsBurger />}
            <SafeAreaFrame insets={size === "large" ? { left: 256 } : { top: 60 }}>
                {children}
            </SafeAreaFrame>
        </Fragment>
    );
}

export function MainMenuAsSideBar() {
    const theme = useTheme();
    const menuCss = css({
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width: 256,
        zIndex: 1000,
        borderRight: `1px solid ${theme.divider}`,
    });
    return (
        <div css={menuCss}>
            <MainMenu />
        </div>
    );
}

export function MainMenuAsBurger() {
    const menuCss = css({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 60,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        backdropFilter: "blur(10px)",
    });

    const modalCss = css({
        width: 256,
        padding: 0,
        margin: 0,
    });

    const [active, setActive] = useState(false);

    const pathname = useLocationPathname();
    useEffect(() => {
        setActive(false);
    }, [pathname]);

    return (
        <Fragment>
            <div css={menuCss}>
                <IconButton icon="menu" action={() => setActive(true)} role="minor" size="big" />
            </div>
            <Modal active={active} onClose={() => setActive(false)} position="start" css={modalCss}>
                <MainMenu />
            </Modal>
        </Fragment>
    );
}

export function MainMenu() {
    const containerCss = css({
        display: "flex",
        flexDirection: "column",
        height: "100%",
    });

    return (
        <Await>
            <div css={containerCss}>
                <Spacer scale={8} />
                <WithSpace horizontal={8}>
                    <a href={loggedInHomeRoute.getLocation()}>
                        <Asset src={logo} />
                    </a>
                </WithSpace>
                <Spacer scale={8} />
                <WithSpace horizontal={2}>
                    <GeneralMenuItems />
                    <ProjectMenuItems />
                    <ManagerMenuItems />
                    <Spacer scale={8} fill />
                </WithSpace>
                <MainMenuBottom />
            </div>
        </Await>
    );
}

function GeneralMenuItems() {
    return (
        <Fragment>
            <Menu>
                <MenuItem
                    label="La procédure d'adoption"
                    icon="map"
                    action={processRoute.getLocation()}
                    selected={useIsRouteActive(processRoute)}
                />
            </Menu>
        </Fragment>
    );
}

function ProjectMenuItems() {
    const selectedProjet = useCurrentProject();
    const content = selectedProjet ? (
        <WithSelectedProjetMenuItems />
    ) : (
        <WithoutSelectedProjetMenuItems />
    );
    return <OnlyWithPermission permission={Permission.Candidate}>{content}</OnlyWithPermission>;
}

function WithSelectedProjetMenuItems() {
    const selectedProjet = useCurrentProject();
    if (!selectedProjet) return null;
    return (
        <Fragment>
            <Spacer scale={4} />
            <Divider />
            <Spacer scale={4} />
            <Text variant="minor">{selectedProjet.label}</Text>
            <Spacer scale={2} />
            <Menu>
                <MenuItem
                    label="Acteurs de mon projet"
                    icon="contacts"
                    action={contactsRoute.getLocation()}
                    selected={useIsRouteActive(contactsNavigator)}
                />
            </Menu>
        </Fragment>
    );
}

function WithoutSelectedProjetMenuItems() {
    return (
        <Menu>
            <MenuItem
                label="Commencer mon projet"
                icon="plus"
                action={createProjectRoute.getLocation()}
                selected={useIsRouteActive(createProjectRoute)}
            />
        </Menu>
    );
}

function ManagerMenuItems() {
    return (
        <Fragment>
            <Spacer scale={4} />
            <Divider />
            <Spacer scale={4} />
            <Text variant="minor">Gestion de la plateforme</Text>
            <Spacer scale={2} />
            <Menu>
                <OnlyWithPermission permission={Permission.UsersManager}>
                    <MenuItem
                        label="Utilisateurs"
                        icon="users"
                        action={usersRoute.getLocation()}
                        selected={useIsRouteActive(usersNavigator)}
                    />
                </OnlyWithPermission>
                <OnlyWithPermission permission={Permission.Speaker}>
                    <MenuItem
                        label="Mailing"
                        icon="email"
                        action={mailingRoute.getLocation()}
                        selected={useIsRouteActive(mailingNavigator)}
                    />
                </OnlyWithPermission>
            </Menu>
        </Fragment>
    );
}

function MainMenuBottom() {
    const [onLogout, logoutSubmission] = useSubmit(async () => {
        await callOutgoingRequest(logout());
        navigate(indexRoute.getLocation());
    }, []);
    return (
        <Fragment>
            <Spacer scale={4} fill />
            <Divider />
            <Spacer scale={4} />
            <Menu>
                <MenuItem
                    label="Réglages"
                    icon="settings"
                    action={settingsRoute.getLocation()}
                    selected={useIsRouteActive(settingsRoute)}
                />
            </Menu>
            <Spacer scale={4} />
        </Fragment>
    );
}
